/**
 * The buyer coach. A buyer talks an order through BEFORE it goes to the vendor, and the
 * conversation travels with the bill to Greg. Mercedes + Greg, 2026-09-17.
 *
 * Two places, one component:
 *   - the "Plan a purchase" tab (no documentId): a list of plans and a chat.
 *   - on an invoice bill (documentId): the plan attached to it, a picker to attach one
 *     that was started earlier, or a fresh plan tied to this bill. Greg can ask the coach
 *     a follow-up here while he approves.
 *
 * ⚠️ POLLS ONLY WHILE AN ANSWER IS BEING WRITTEN. An always-on interval against a
 * Neon-backed endpoint is a slow tax on every open tab.
 * ⚠️ Everything a person typed is rendered as React nodes (ChatText), never innerHTML.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare, Plus, Send, Link2, X, Loader2, Archive, CheckCircle2, FileText } from 'lucide-react';
import { apInvoiceCoach, apVendorProfiles } from '../services/api';
import VendorProfileCard from './VendorProfileCard';
import MicButton from './MicButton';
import { ChatText } from './InvoiceContext';
import { PlanFacts } from './BuyFacts';
import OrderForm, { ChoiceCards } from './OrderForm';

const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300, #d1d5db)',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
};

const labelStyle = {
  fontSize: 11, fontWeight: 700, color: 'var(--gray-500, #6b7280)',
  textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
};

// ⚠️ Local date, never toISOString(). After 8pm Eastern toISOString returns UTC and
// rolls the day forward, so a placement stamped tonight would read as tomorrow on
// the PO panel and on Greg's approval line the next morning.
function todayLocalIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const STARTERS = [
  'When did we last order this, from whom, how much, and at what price?',
  'How much have we sold per month since then?',
  'Did our sales keep up with what we bought?',
  'Is this quote in line with what we have paid before?',
  'Why would we buy this from this vendor instead of our usual one?',
];

function Thread({ planId, onChanged, compact, onLifecycle }) {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [micStop, setMicStop] = useState(0);
  const [poDraft, setPoDraft] = useState(null); // { poNumber, expectedAmount, expectedDate, note }
  const [converting, setConverting] = useState(false);
  // The vendor's card, opened over the chat so what the buyer learns mid-conversation can
  // be put on it without leaving the order (Greg, 2026-10-09). Closing it re-reads the
  // facts strip, since that is what a change on the card changes here.
  const [vendorCard, setVendorCard] = useState(null);
  const [factsTick, setFactsTick] = useState(0);
  const openVendorCard = async () => {
    try { setVendorCard((await apVendorProfiles('get', { vendor: plan.vendor_name })).vendor); }
    catch (e) { setErr(e.message); }
  };
  const closeVendorCard = () => { setVendorCard(null); setFactsTick((n) => n + 1); };
  // Picking one of the coach's option cards: the choice is recorded on the plan, and an
  // option that is an actual order opens the convert-to-PO boxes already filled in, so
  // the path is form, options, pick, order placed (Greg, 2026-10-09).
  const choose = async (o) => {
    try {
      await apInvoiceCoach('choose', { planId, option: o });
      await load();
      if (o.quantity != null) {
        setPoDraft({
          poNumber: '', expectedAmount: o.total != null ? String(o.total) : '',
          expectedDate: todayLocalIso(),
          note: `Chose: ${o.label}${o.vendor ? ` (${o.vendor})` : ''}`,
        });
      }
    } catch (e) { setErr(e.message); }
  };
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await apInvoiceCoach('get', { planId });
      setPlan(r.plan); setErr('');
    } catch (e) { setErr(e.message); }
  }, [planId]);

  useEffect(() => { setPlan(null); load(); }, [load]);

  const working = plan && plan.job_state !== 'idle' && !plan.job_stale;
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [working, load]);
  const msgCount = plan ? (plan.messages || []).length : 0;
  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: 'nearest' });
  }, [msgCount, working]);

  const send = async (q) => {
    const message = String(q != null ? q : text).trim();
    if (!message || busy || working) return;
    setBusy(true); setMicStop((n) => n + 1);
    try {
      await apInvoiceCoach('send', { planId, message });
      setText('');
      await load();
      onChanged && onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (!plan) {
    return <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>{err || 'Loading the conversation…'}</div>;
  }
  const messages = (plan.messages || []).filter((m) => m.text);
  const chosen = [...(plan.messages || [])].reverse().find((m) => m && m.role === 'system' && m.kind === 'choice');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: compact ? 0 : 420 }}>
      {!compact && plan.state === 'plan' && (
        <OrderForm planId={planId} plan={plan} working={working} onSent={async () => { await load(); onChanged && onChanged(); }} />
      )}
      {/* What the team has saved for this vendor or product, and anything the coach has
          just offered to save. Re-read after every message, since that is when a proposal
          can appear. */}
      <PlanFacts planId={planId} vendorName={plan.vendor_name} reloadKey={`${msgCount}-${working ? 1 : 0}-${factsTick}`} />
      {plan.vendor_name && (
        <div style={{ margin: '-4px 0 8px', textAlign: 'right' }}>
          <button className="btn btn-ghost" style={{ padding: 0 }} onClick={openVendorCard}>
            Open {plan.vendor_name}'s Vendor Card
          </button>
        </div>
      )}
      {vendorCard && (
        <div className="modal-overlay" onClick={closeVendorCard}>
          <div className="modal" style={{ maxWidth: 760, width: '94%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Vendor</h2>
              <button className="modal-close" onClick={closeVendorCard}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '74vh', overflowY: 'auto' }}>
              <VendorProfileCard vendor={vendorCard} />
            </div>
          </div>
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto', maxHeight: compact ? 420 : 560, padding: '4px 2px' }}>
        {messages.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)', margin: '8px 0 12px' }}>
            Ask anything about what we have bought and sold before you place the order. A good start:
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {STARTERS.map((s) => (
                <button key={s} className="btn btn-outline" style={{ fontSize: 12, textAlign: 'left' }}
                  onClick={() => setText(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', margin: '8px 0',
          }}>
            <div style={{
              maxWidth: m.choices ? '100%' : '86%', borderRadius: 10, padding: '8px 12px',
              background: m.role === 'user' ? '#fff3e6' : '#f3f6f9',
              border: `1px solid ${m.role === 'user' ? '#f6d4b8' : '#dbe5ee'}`,
            }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--gray-500, #6b7280)', marginBottom: 3 }}>
                {m.role === 'user' ? (m.by || 'You') : 'Buyer coach'}
                {m.role === 'assistant' && m.lookups && m.lookups.length > 0 && (
                  <span style={{ fontWeight: 400 }}>
                    {' '}· looked at {[...new Set(m.lookups.map((l) => l.tool.replace(/_/g, ' ')))].join(', ')}
                  </span>
                )}
              </div>
              <ChatText text={m.text} />
              {m.choices && (
                <ChoiceCards choices={m.choices} disabled={busy || working}
                  onChoose={!compact && plan.state === 'plan' ? choose : null} />
              )}
              {m.choices && chosen && i === messages.length - 1 && (
                <div style={{ fontSize: 12.5, color: '#1c7c43', marginTop: 8 }}>
                  Chosen: {chosen.data && chosen.data.label}{chosen.by ? `, by ${String(chosen.by).split(' ')[0]}` : ''}
                </div>
              )}
            </div>
          </div>
        ))}
        {working && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--gray-500, #6b7280)', margin: '8px 0' }}>
            <Loader2 size={14} className="spin" /> Looking through the bills and the sales…
          </div>
        )}
        {plan.job_stale && (
          <div style={{ fontSize: 12.5, color: '#9a4a1c', margin: '8px 0' }}>
            The last question never got an answer. Ask it again.
          </div>
        )}
        {plan.last_error && !working && (
          <div style={{ fontSize: 12.5, color: '#b42318', margin: '8px 0' }}>
            The coach could not answer: {plan.last_error}
          </div>
        )}
        <div ref={endRef} />
      </div>
      {err && <div style={{ fontSize: 12.5, color: '#b42318', marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginTop: 8 }}>
        <textarea style={{ ...inputStyle, minHeight: 44, resize: 'vertical' }}
          placeholder={working ? 'Working on the last question…' : 'Ask about this order'}
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <MicButton onTranscript={(t) => setText((x) => (x ? `${x} ${t}` : t))} stopWhen={micStop} size={15} />
        <button className="btn btn-primary" disabled={busy || working || !text.trim()} onClick={() => send()}
          title="Send">
          <Send size={14} />
        </button>
      </div>

      {/* ⚠️ CONVERT ROWS DEPEND ON THE PLAN'S STATE, NEVER ON A LOCAL FLAG. The server
          computes it from what has happened to the plan (lifecycle markers + document_id)
          so a stale panel cannot show a "mark as ordered" button on a plan that has
          already become an invoice. */}
      {!compact && plan.state === 'plan' && !poDraft && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" style={{ fontSize: 12.5 }}
            onClick={() => setPoDraft({
              poNumber: '', expectedAmount: '',
              // Mercedes, 2026-09-18: they may not know when the bill is due at the time
              // the order goes out - the invoice comes days later - so this defaults to
              // today (the placement date) and can be updated when the real due date is
              // on the invoice.
              expectedDate: todayLocalIso(),
              note: '',
            })}>
            <CheckCircle2 size={14} style={{ marginRight: 6 }} /> Order placed - convert to PO
          </button>
        </div>
      )}
      {!compact && plan.state === 'plan' && poDraft && (
        <div style={{
          marginTop: 12, padding: 12, background: '#fff7ed',
          border: '1px solid #f6d4b8', borderRadius: 8,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: 'var(--navy, #00395C)' }}>
            The order went to {plan.vendor_name || 'the vendor'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-600, #4b5563)', marginBottom: 10 }}>
            This becomes a PO you can find on your My POs tab. Nobody else sees it there
            until you convert it to an invoice.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
            <div>
              <div style={labelStyle}>PO number (optional)</div>
              <input style={inputStyle} placeholder="e.g. 4021" value={poDraft.poNumber}
                onChange={(e) => setPoDraft({ ...poDraft, poNumber: e.target.value })} />
            </div>
            <div>
              <div style={labelStyle}>Expected amount (optional)</div>
              <input style={inputStyle} placeholder="0.00" inputMode="decimal"
                value={poDraft.expectedAmount}
                onChange={(e) => setPoDraft({ ...poDraft, expectedAmount: e.target.value })} />
            </div>
            <div>
              <div style={labelStyle}>Due date</div>
              <input style={inputStyle} type="date" value={poDraft.expectedDate}
                onChange={(e) => setPoDraft({ ...poDraft, expectedDate: e.target.value })} />
              <div style={{ fontSize: 10.5, color: 'var(--gray-500, #6b7280)', marginTop: 3 }}>
                today by default, update when the invoice arrives
              </div>
            </div>
          </div>
          <textarea style={{ ...inputStyle, minHeight: 44 }} placeholder="Anything else about the order (optional)"
            value={poDraft.note} onChange={(e) => setPoDraft({ ...poDraft, note: e.target.value })} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" disabled={converting}
              onClick={async () => {
                setConverting(true); setErr('');
                try {
                  await apInvoiceCoach('convert-to-po', {
                    planId, poNumber: poDraft.poNumber, expectedAmount: poDraft.expectedAmount,
                    expectedDate: poDraft.expectedDate, note: poDraft.note,
                    vendorName: plan.vendor_name || null,
                  });
                  setPoDraft(null);
                  await load();
                  onChanged && onChanged();
                  onLifecycle && onLifecycle({ state: 'po' });
                } catch (e) { setErr(e.message); }
                setConverting(false);
              }}>
              <CheckCircle2 size={14} style={{ marginRight: 6 }} /> Save as a PO
            </button>
            <button className="btn btn-ghost" disabled={converting} onClick={() => setPoDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
      {!compact && plan.state === 'po' && (
        <div style={{
          marginTop: 12, padding: 12, background: '#f5f8fc',
          border: '1px solid #c9dcee', borderRadius: 8,
        }}>
          <div style={{ fontSize: 12.5, color: 'var(--gray-700, #374151)', marginBottom: 8 }}>
            Ordered{plan.po && plan.po.poNumber ? ` (PO #${plan.po.poNumber})` : ''}
            {plan.po && plan.po.expectedAmount != null
              ? `, expected around $${Number(plan.po.expectedAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : ''}
            {plan.po && plan.po.expectedDate ? `, due ${plan.po.expectedDate}` : ''}.
            When the vendor's invoice arrives, convert this to an invoice bill.
          </div>
          <button className="btn btn-primary" style={{ fontSize: 12.5 }} disabled={converting}
            onClick={async () => {
              setConverting(true); setErr('');
              try {
                const r = await apInvoiceCoach('convert-to-invoice', { planId });
                await load();
                onChanged && onChanged();
                onLifecycle && onLifecycle({ state: 'invoiced', documentId: r.documentId });
              } catch (e) { setErr(e.message); }
              setConverting(false);
            }}>
            <FileText size={14} style={{ marginRight: 6 }} /> Convert PO to invoice
          </button>
        </div>
      )}
      {!compact && plan.state === 'invoiced' && plan.document_id && (
        <div style={{
          marginTop: 12, padding: '9px 12px', background: '#e8f5ec',
          border: '1px solid #b7e0c6', borderRadius: 8, color: '#1c7c43',
          fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FileText size={14} />
          On invoice approval #{plan.document_id}. Everything discussed here is on the bill for Greg.
        </div>
      )}
    </div>
  );
}

// The whole "Plan an Order" tab. Mercedes, 2026-09-18: this list is private - my plans
// only. The server filters to created_by; the browser only reinforces it.
export function PlanWorkspace({ vendors = [], categories = [], onChanged }) {
  const [plans, setPlans] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(null); // { title, vendorName, category, message }
  const [createMicStop, setCreateMicStop] = useState(0);

  const loadList = useCallback(async () => {
    try {
      const r = await apInvoiceCoach('list', { mineOnly: true, state: 'plan' });
      setPlans(r.plans || []);
      setOpenId((cur) => cur || ((r.plans || [])[0] || {}).id || null);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  const bump = useCallback(() => { loadList(); onChanged && onChanged(); }, [loadList, onChanged]);

  const create = async () => {
    setCreateMicStop((n) => n + 1);
    try {
      const r = await apInvoiceCoach('create', {
        title: creating.title, vendorName: creating.vendorName,
        category: (categories.find((c) => c.label === creating.category) || {}).account || creating.category,
        message: creating.message,
      });
      setCreating(null);
      setOpenId(r.planId);
      bump();
    } catch (e) { setErr(e.message); }
  };

  const archive = async (id) => {
    try { await apInvoiceCoach('archive', { planId: id }); setOpenId(null); bump(); }
    catch (e) { setErr(e.message); }
  };

  const open = plans.find((p) => p.id === openId);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 16, alignItems: 'start' }}
      className="invoice-coach-grid">
      <div style={{ background: 'white', border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8 }}>
        <div style={{ padding: 10, borderBottom: '1px solid var(--gray-100, #f3f4f6)' }}>
          <button className="btn btn-primary" style={{ width: '100%' }}
            onClick={() => setCreating({ title: '', vendorName: '', category: '', message: '' })}>
            <Plus size={14} style={{ marginRight: 6 }} /> Plan a purchase
          </button>
        </div>
        {plans.length === 0 && (
          <div style={{ padding: 12, fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>
            No plans yet. Start one before the order goes to the vendor. Only you see this list.
          </div>
        )}
        {plans.map((p) => (
          <button key={p.id} onClick={() => { setCreating(null); setOpenId(p.id); }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
              background: p.id === openId ? '#fff3e6' : 'transparent',
              borderBottom: '1px solid var(--gray-100, #f3f4f6)', padding: '9px 12px',
            }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy, #00395C)' }}>{p.title}</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
              {[p.vendor_name, p.created_by_name, p.updated_label].filter(Boolean).join(' · ')}
            </div>
            {p.document_id && (
              <div style={{ fontSize: 11, color: '#1c7c43', fontWeight: 600 }}>On bill #{p.document_id}</div>
            )}
          </button>
        ))}
      </div>

      <div style={{ background: 'white', border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8, padding: 14 }}>
        {err && <div style={{ fontSize: 12.5, color: '#b42318', marginBottom: 8 }}>{err}</div>}
        {creating ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy, #00395C)' }}>What are you thinking of ordering?</div>
            <input style={inputStyle} placeholder="A short name, e.g. September shrimp order" value={creating.title}
              onChange={(e) => setCreating({ ...creating, title: e.target.value })} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <div>
                <input style={inputStyle} list="coach-vendors" placeholder="Vendor (optional)" value={creating.vendorName}
                  onChange={(e) => setCreating({ ...creating, vendorName: e.target.value })} />
                <datalist id="coach-vendors">{vendors.map((v) => <option key={v.name} value={v.name} />)}</datalist>
              </div>
              <div>
                <input style={inputStyle} list="coach-cats" placeholder="What it is for (optional)" value={creating.category}
                  onChange={(e) => setCreating({ ...creating, category: e.target.value })} />
                <datalist id="coach-cats">{categories.map((c) => <option key={c.account} value={c.label} />)}</datalist>
              </div>
            </div>
            <textarea style={{ ...inputStyle, minHeight: 80 }}
              placeholder="Your first question, e.g. Cox's quoted 16/20 Key West pinks at $11.25 a pound for 800 lb. Is that in line, and do we need that much?"
              value={creating.message} onChange={(e) => setCreating({ ...creating, message: e.target.value })} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <MicButton
                onTranscript={(t) => setCreating((c) => ({ ...c, message: c.message ? `${c.message} ${t}` : t }))}
                stopWhen={createMicStop} size={15} />
              <button className="btn btn-primary" disabled={!creating.title.trim() && !creating.message.trim()} onClick={create}>
                <MessageSquare size={14} style={{ marginRight: 6 }} /> Start the plan
              </button>
              <button className="btn btn-ghost" onClick={() => setCreating(null)}>Cancel</button>
            </div>
          </div>
        ) : open ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy, #00395C)' }}>{open.title}</div>
              <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
                {[open.vendor_name, `started by ${open.created_by_name}`].filter(Boolean).join(' · ')}
              </span>
              {!open.document_id && (
                <button className="btn btn-ghost" style={{ marginLeft: 'auto', fontSize: 12 }} onClick={() => archive(open.id)}>
                  <Archive size={13} style={{ marginRight: 4 }} /> Archive
                </button>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', marginBottom: 8 }}>
              {open.document_id
                ? `This plan is on invoice approval #${open.document_id}, so Greg sees it beside the bill.`
                : 'When the order goes to the vendor, mark it as ordered below. It moves to your My POs tab, still only visible to you.'}
            </div>
            <Thread key={open.id} planId={open.id} onChanged={bump} onLifecycle={bump} />
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>Pick a plan on the left, or start one.</div>
        )}
      </div>
    </div>
  );
}

// The My POs tab: plans that have been marked as ordered but do not yet have an invoice.
// Same shape as PlanWorkspace, without the "new plan" side (a PO comes from a plan, never
// from nothing). "Convert PO to invoice" lives on the thread itself.
export function POWorkspace({ onChanged, onOpenDocument }) {
  const [pos, setPos] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState('');

  const loadList = useCallback(async () => {
    try {
      const r = await apInvoiceCoach('list', { mineOnly: true, state: 'po' });
      setPos(r.plans || []);
      setOpenId((cur) => cur || ((r.plans || [])[0] || {}).id || null);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const onLifecycle = useCallback(async (info) => {
    await loadList();
    onChanged && onChanged();
    if (info && info.state === 'invoiced' && info.documentId && onOpenDocument) {
      onOpenDocument(info.documentId);
    }
  }, [loadList, onChanged, onOpenDocument]);

  const open = pos.find((p) => p.id === openId);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 16, alignItems: 'start' }}
      className="invoice-coach-grid">
      <div style={{ background: 'white', border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8 }}>
        <div style={{ padding: 10, borderBottom: '1px solid var(--gray-100, #f3f4f6)', fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>
          POs you have placed. Only you see this list, until you turn one into an invoice.
        </div>
        {pos.length === 0 && (
          <div style={{ padding: 12, fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>
            Nothing here yet. When you place an order from a plan, save it as a PO and it
            will show up here until the invoice arrives.
          </div>
        )}
        {pos.map((p) => (
          <button key={p.id} onClick={() => setOpenId(p.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
              background: p.id === openId ? '#fff3e6' : 'transparent',
              borderBottom: '1px solid var(--gray-100, #f3f4f6)', padding: '9px 12px',
            }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy, #00395C)' }}>{p.title}</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
              {[p.vendor_name, p.po && p.po.poNumber ? `PO #${p.po.poNumber}` : null, p.updated_label]
                .filter(Boolean).join(' · ')}
            </div>
          </button>
        ))}
      </div>

      <div style={{ background: 'white', border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8, padding: 14 }}>
        {err && <div style={{ fontSize: 12.5, color: '#b42318', marginBottom: 8 }}>{err}</div>}
        {open ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy, #00395C)' }}>{open.title}</div>
              <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
                {[open.vendor_name, `started by ${open.created_by_name}`].filter(Boolean).join(' · ')}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', marginBottom: 8 }}>
              Convert this to an invoice when the vendor's bill arrives. Everything discussed
              in the plan follows the bill to Greg.
            </div>
            <Thread key={open.id} planId={open.id} onChanged={loadList} onLifecycle={onLifecycle} />
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>Pick a PO on the left.</div>
        )}
      </div>
    </div>
  );
}

// On an invoice bill: the attached plan, or a way to attach or start one.
export default function InvoiceCoach({ documentId, vendorName, category, canAttach, onChanged }) {
  const [attached, setAttached] = useState(undefined); // undefined = loading, null = none
  const [candidates, setCandidates] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const mine = await apInvoiceCoach('list', { documentId });
      setAttached((mine.plans || [])[0] || null);
      if (canAttach) {
        const free = await apInvoiceCoach('list', { unattached: true });
        setCandidates(free.plans || []);
      }
    } catch (e) { setErr(e.message); setAttached(null); }
  }, [documentId, canAttach]);
  useEffect(() => { load(); }, [load]);

  const attach = async (planId) => {
    setBusy(true);
    try { await apInvoiceCoach('attach', { planId, documentId }); await load(); onChanged && onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const detach = async () => {
    setBusy(true);
    try { await apInvoiceCoach('attach', { planId: attached.id, documentId: null }); await load(); onChanged && onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const startHere = async () => {
    setBusy(true);
    try {
      await apInvoiceCoach('create', {
        title: vendorName ? `${vendorName} invoice` : 'Invoice plan',
        vendorName, category, documentId,
      });
      await load(); onChanged && onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (attached === undefined) {
    return <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>Loading…</div>;
  }

  // Plans for this vendor first: they are almost always the one being looked for.
  const sorted = [...candidates].sort((a, b) => {
    const va = vendorName && a.vendor_name && a.vendor_name.toLowerCase() === vendorName.toLowerCase() ? 0 : 1;
    const vb = vendorName && b.vendor_name && b.vendor_name.toLowerCase() === vendorName.toLowerCase() ? 0 : 1;
    return va - vb;
  });

  return (
    <div style={{ padding: 16 }}>
      {err && <div style={{ fontSize: 12.5, color: '#b42318', marginBottom: 8 }}>{err}</div>}
      {attached ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13.5, color: 'var(--navy, #00395C)' }}>{attached.title}</strong>
            <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>started by {attached.created_by_name}</span>
            {canAttach && (
              <button className="btn btn-ghost" style={{ marginLeft: 'auto', fontSize: 12 }} disabled={busy} onClick={detach}>
                <X size={13} style={{ marginRight: 4 }} /> Take it off this bill
              </button>
            )}
          </div>
          <Thread planId={attached.id} onChanged={onChanged} compact />
        </>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--gray-600, #4b5563)' }}>
            No plan on this bill. If the buyer talked this order through with the coach, attach it
            so it sits beside the invoice when Greg approves it. A routine bill does not need one.
          </div>
          {canAttach && sorted.length > 0 && (
            <div>
              <select style={inputStyle} defaultValue="" disabled={busy}
                onChange={(e) => { if (e.target.value) attach(Number(e.target.value)); }}>
                <option value="">Attach a plan that was started earlier…</option>
                {sorted.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}{p.vendor_name ? ` · ${p.vendor_name}` : ''} · {p.created_by_name} · {p.updated_label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <button className="btn btn-outline" disabled={busy} onClick={startHere}>
              <Link2 size={14} style={{ marginRight: 6 }} /> Talk this bill through with the coach
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
